/* global globalThis */
import React from "react";
import PropTypes from "prop-types";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getPaginationRowModel,
  getSortedRowModel,
  getExpandedRowModel,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
} from "@mui/material";
import FocusableScrollContainer from "../FocusableScrollContainer";
import "./commonTable.css";

const getRowSelectionId = (row, index) => {
  if (!row || typeof row !== "object") return String(index);

  return (
    row.id ??
    row.memberId ??
    row.member?.memberId ??
    row.manualTask?.id ??
    String(index)
  );
};

const getContainerHeight = ({ tableHeight, byMembers, filtersVisible }) => {
  if (tableHeight != null) {
    return typeof tableHeight === "number" ? `${tableHeight}px` : tableHeight;
  }
  if (byMembers) return "calc(var(--real-vh, 100vh) - 130px)";
  if (filtersVisible) return "calc(var(--real-vh, 100vh) - 230px)";
  return "calc(var(--real-vh, 100vh) - 145px)";
};

const getHeaderContent = (header, isSortable) => {
  const hasHeaderName = !!header.column.columnDef.headerName;
  const hasHeader = !!header.column.columnDef.header;

  if (isSortable || (!hasHeaderName && hasHeader)) {
    return flexRender(header.column.columnDef.header, header.getContext());
  }
  if (hasHeaderName) {
    return header.column.columnDef.headerName;
  }
  return null;
};

const pickDefined = (external, fallback) =>
  external === undefined ? fallback : external;

const getExpandedState = (getSubRows, expanded) =>
  getSubRows ? { expanded } : {};

const getRowSelectionEnabled = (getSubRows, enableRowSelection) =>
  getSubRows
    ? (row) => enableRowSelection && row.depth === 0
    : enableRowSelection;

const getSubRowsOptions = (getSubRows, setExpanded) =>
  getSubRows
    ? {
        getSubRows,
        onExpandedChange: setExpanded,
        getExpandedRowModel: getExpandedRowModel(),
      }
    : {};

const getPaginationOptions = (paginate, pageSize) =>
  paginate
    ? {
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
          pagination: {
            pageSize: pageSize || 30,
            pageIndex: 0,
          },
        },
      }
    : {};

const CommonTable = React.forwardRef(
  (
    {
      columns,
      data = [],
      getRowClassName,
      onRowClick,
      hideHeader = false,
      showHeaderOnly = false,
      enableRowSelection = false,
      onRowSelectionChange,
      selectedRows = [],
      className,
      filtersVisible,
      loading,
      isMemberMonitoring,
      byMembers,
      byProvider,
      pageSize = 100,
      sorting: externalSorting,
      onSortingChange: externalOnSortingChange,
      manualSorting = false,
      tableHeight,
      highlightedTaskId,
      paginate = true,
      onLoadMoreItems,
      itemCount,
      useColumnMinWidth = false,
      getSubRows,
      ...props
    },
    ref,
  ) => {
    const [internalSorting, setInternalSorting] = React.useState([]);
    const [rowSelection, setRowSelection] = React.useState({});
    // Rows with sub-rows (e.g. family members under a primary member) start
    // collapsed; only used when getSubRows is provided.
    const [expanded, setExpanded] = React.useState({});

    const sorting = pickDefined(externalSorting, internalSorting);
    const setSorting = pickDefined(externalOnSortingChange, setInternalSorting);

    const table = useReactTable({
      data,
      columns,
      state: {
        sorting,
        rowSelection,
        ...getExpandedState(getSubRows, expanded),
      },
      // Selection is keyed by top-level data index (see
      // onRowSelectionChange), so sub-rows can't be selected themselves.
      enableRowSelection: getRowSelectionEnabled(
        getSubRows,
        enableRowSelection,
      ),
      ...getSubRowsOptions(getSubRows, setExpanded),
      onRowSelectionChange: (updater) => {
        const newSelection =
          typeof updater === "function" ? updater(rowSelection) : updater;
        setRowSelection(newSelection);

        if (onRowSelectionChange) {
          const selectedIds = Object.keys(newSelection)
            .filter((i) => newSelection[i] && data[i] !== undefined)
            .map((i) => getRowSelectionId(data[i], i));
          onRowSelectionChange(selectedIds);
        }
      },
      onSortingChange: (updater) => {
        const nextSorting =
          typeof updater === "function" ? updater(sorting) : updater;
        setSorting(nextSorting || []);
      },
      getCoreRowModel: getCoreRowModel(),
      ...(manualSorting ? {} : { getSortedRowModel: getSortedRowModel() }),
      ...getPaginationOptions(paginate, pageSize),
    });

    React.useEffect(() => {
      if (enableRowSelection && selectedRows) {
        const selectedSet = new Set((selectedRows || []).map(String));
        const newRowSelection = {};
        for (let index = 0; index < data.length; index++) {
          const row = data[index];
          const selectionId = String(getRowSelectionId(row, index));
          if (selectedSet.has(selectionId)) {
            newRowSelection[index] = true;
          }
        }
        setRowSelection(newRowSelection);
      }
    }, [selectedRows, data, enableRowSelection]);

    const scrollRef = React.useRef(null);
    const parentRef = scrollRef;

    const rowVirtualizer = useVirtualizer({
      count: table.getRowModel().rows.length,
      getScrollElement: () => parentRef.current,
      estimateSize: () => 56,
      overscan: 50,
      // Rows change height when family sub-rows expand/collapse; measuring
      // inside the ResizeObserver callback re-lays-out the table and trips
      // "ResizeObserver loop completed with undelivered notifications" - defer
      // the measurement to the next animation frame instead.
      useAnimationFrameWithResizeObserver: true,
    });

    const virtualItems = rowVirtualizer.getVirtualItems();

    const bottomSpacerHeight =
      virtualItems.length > 0
        ? rowVirtualizer.getTotalSize() - virtualItems.at(-1).end
        : 0;

    React.useEffect(() => {
      const lastItem = virtualItems.at(-1);

      if (!lastItem) {
        return;
      }

      if (
        lastItem.index >= data.length - 1 &&
        !loading &&
        onLoadMoreItems &&
        (itemCount === undefined || data.length < itemCount)
      ) {
        onLoadMoreItems();
      }
    }, [onLoadMoreItems, virtualItems, data.length, loading, itemCount]);

    React.useEffect(() => {
      const setRealVh = () => {
        document.documentElement.style.setProperty(
          "--real-vh",
          `${globalThis.innerHeight}px`,
        );
      };
      setRealVh();
      globalThis.addEventListener("resize", setRealVh);
      return () => globalThis.removeEventListener("resize", setRealVh);
    }, []);

    React.useImperativeHandle(ref, () => ({
      tableInstance: table,
      scrollElement: scrollRef.current,
      scrollToTop: () => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = 0;
        }
      },
    }));

    const tableRootRef = React.useRef(null);

    // unused variable noDataRow removed

    return (
      <Box
        ref={tableRootRef}
        sx={{
          borderRadius: "12px !important",
          borderBottomLeftRadius: "0 !important",
          borderBottomRightRadius: "0 !important",
          bgcolor: "white",
          overflow: "hidden",
          "& .MuiTableContainer-root": {
            padding: 0,

            overflowY: "auto",
          },
          "& .member-name": {
            color: "#403C6F",
            fontWeight: 700,
            fontStyle: "bold",
            fontSize: "16px",
            lineHeight: "24px",
            letterSpacing: "0%",
            textDecoration: "underline",
            textDecorationStyle: "solid",
            textDecorationThickness: "auto",
            textUnderlineOffset: "2px",
            cursor: "pointer",
            "&:hover": {
              opacity: 0.8,
            },
          },
        }}
      >
        <FocusableScrollContainer
          sx={{
            "& .MuiTableRow-root": {
              position: "relative",
              "&:not(:last-child)::after": {
                content: '""',
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "1.5px",
                backgroundColor: "#E8E8E9",
              },
            },
            "& .MuiTableCell-root": {
              borderBottom: "none",
              padding: "12px 16px",
            },
          }}
        >
          <TableContainer
            ref={scrollRef}
            sx={() => {
              const containerHeight = getContainerHeight({
                tableHeight,
                byMembers,
                filtersVisible,
              });
              return {
                height: containerHeight,
                maxHeight: containerHeight,
                overflowY: "auto",
                "& .MuiTableRow-root": {
                  position: "relative",
                  "&:not(:last-child)::after": {
                    content: '""',
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: "1.5px",
                    backgroundColor: "#E8E8E9",
                  },
                },
                "& .MuiTableCell-root": {
                  borderBottom: "none",
                  padding: "12px 16px",
                },
              };
            }}
          >
            <Table
              className={`common-table-container ${className || ""}`}
              {...props}
              stickyHeader
            >
              {!hideHeader && (
                <TableHead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        const isSortable = header.column.getCanSort();

                        let dataColId;
                        if (
                          header.column.id === "latestDateAdded" ||
                          header.column.id === "actions"
                        ) {
                          dataColId = header.column.id;
                        }

                        const headerContent = getHeaderContent(
                          header,
                          isSortable,
                        );
                        return (
                          <TableCell
                            key={header.id}
                            className="common-table-header-cell"
                            onClick={(e) => {
                              if (!isSortable) return;
                              const target = e.target;
                              // only trigger sorting if we clicked the sort icon itself or something within it
                              if (target.closest("[data-sort-icon]")) {
                                header.column.getToggleSortingHandler()(e);
                              }
                            }}
                            sx={{
                              cursor: "default",
                              userSelect: "none",
                              fontWeight: 700,
                              fontSize: "16px",
                              lineHeight: "24px",
                              letterSpacing: "0px",
                              backgroundColor: "#CFCEDA",
                              position: "sticky",
                              top: 0,
                              zIndex: 2,
                            }}
                            {...(dataColId ? { "data-col-id": dataColId } : {})}
                          >
                            {!header.isPlaceholder && <>{headerContent}</>}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableHead>
              )}

              <TableBody>
                {data.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      sx={{
                        textAlign: "center",
                        padding: "40px 16px",
                        color: "#grey",
                        fontSize: "0.8rem",
                      }}
                    >
                      No Records found
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {virtualItems.length > 0 ? (
                      <TableRow
                        style={{ height: `${virtualItems[0].start}px` }}
                      >
                        <TableCell
                          colSpan={columns.length}
                          style={{ padding: 0, border: 0 }}
                        />
                      </TableRow>
                    ) : null}
                    {virtualItems.map((virtualRow) => {
                      const row = table.getRowModel().rows[virtualRow.index];
                      if (!row) return null;

                      const isHighlighted =
                        highlightedTaskId &&
                        row.original.id?.toString() ===
                          highlightedTaskId?.toString();

                      return (
                        <TableRow
                          key={row.id}
                          data-index={virtualRow.index}
                          ref={rowVirtualizer.measureElement}
                          hover
                          className={`common-table-row ${
                            getRowClassName ? getRowClassName(row.original) : ""
                          } ${isHighlighted ? "highlighted-task-row" : ""}`}
                          onClick={() => onRowClick?.(row.original)}
                          sx={
                            isHighlighted ? { backgroundColor: "#F4EFFE" } : {}
                          }
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell
                              key={cell.id}
                              {...(cell.column.id === "latestDateAdded" ||
                              cell.column.id === "actions"
                                ? { "data-col-id": cell.column.id }
                                : {})}
                              sx={{
                                ...(useColumnMinWidth && {
                                  minWidth: cell.column.getSize(),
                                }),
                              }}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                    {virtualItems.length > 0 ? (
                      <TableRow
                        style={{
                          height: `${bottomSpacerHeight}px`,
                        }}
                      >
                        <TableCell
                          colSpan={columns.length}
                          style={{ padding: 0, border: 0 }}
                        />
                      </TableRow>
                    ) : null}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </FocusableScrollContainer>

        <AriaCloneCleanup
          rows={table.getRowModel().rows}
          rootRef={tableRootRef}
        />
      </Box>
    );
  },
);

CommonTable.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  getRowClassName: PropTypes.func,
  onRowClick: PropTypes.func,
  hideHeader: PropTypes.bool,
  showHeaderOnly: PropTypes.bool,
  enableRowSelection: PropTypes.bool,
  onRowSelectionChange: PropTypes.func,
  selectedRows: PropTypes.array,
  className: PropTypes.string,
  filtersVisible: PropTypes.bool,
  loading: PropTypes.bool,
  isMemberMonitoring: PropTypes.bool,
  byMembers: PropTypes.bool,
  byProvider: PropTypes.bool,
  pageSize: PropTypes.number,
  sorting: PropTypes.array,
  onSortingChange: PropTypes.func,
  manualSorting: PropTypes.bool,
  tableHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  highlightedTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  paginate: PropTypes.bool,
  disableScrollAnchoring: PropTypes.bool,
  onLoadMoreItems: PropTypes.func,
  itemCount: PropTypes.number,
  useColumnMinWidth: PropTypes.bool,
  getSubRows: PropTypes.func,
};

export default React.memo(CommonTable);

const AriaCloneCleanup = ({ rows, rootRef }) => {
  React.useEffect(() => {
    const root = rootRef?.current;
    if (!root) return;
    const selector =
      'div[data-mui-internal-clone-element="true"][aria-label],span[data-mui-internal-clone-element="true"][aria-label]';

    const clean = () => {
      const clones = root.querySelectorAll(selector);
      for (const node of clones) {
        const label = node.getAttribute("aria-label");
        if (!label) continue;
        if (!node.getAttribute("title")) node.setAttribute("title", label);
        node.removeAttribute("aria-label");
      }
    };

    clean();
    const mo = new MutationObserver(() => clean());
    mo.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-label"],
    });
    return () => mo.disconnect();
  }, [rows, rootRef]);
  return null;
};

AriaCloneCleanup.propTypes = {
  rows: PropTypes.array,
  rootRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
};
